import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { cn } from '../util/designUtil';

const WelcomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useColorTheme();

  useEffect(() => {
    if (user) {
      navigate('/home');
    }
  }, [user, navigate]);

  return (
    <div className="flex flex-col min-h-screen md:px-32">
      {/* Header */}
      {/* <header className="container mx-auto px-1 py-4 flex items-center gap-2">
        <Star className={cn("h-5 w-5", theme.secondary)} />
        <span className={cn("text-lg font-semibold", theme.secondary)}>Nexbii</span>
      </header>
      
      {/* Hero Section */}
      <section className="w-full pt-6 md:pt-12 pb-0">
        <div className="container mx-auto px-1">
          <div className="grid gap-4 lg:grid-cols-2 lg:gap-4 items-center">
            <div className="flex flex-col justify-center space-y-4">
              <div className="space-y-2 pr-0">
                <h1 className="text-3xl font-bold tracking-tighter sm:text-5xl xl:text-6xl/none">
                  Connect with Your Favorite Influencers in Real-Time
                </h1>
                <p className="max-w-[450px] text-gray-600 md:text-xl">
                  Nexbii lets fans connect with public figures through exclusive one-on-one interactions at a per-minute
                  rate through our unique bidding system.
                </p>
              </div>
              <div className="flex flex-col gap-2 min-[400px]:flex-row">
                <button
                  onClick={() => navigate('/register')}
                  className={cn("px-8 py-2 rounded-md bg-gradient-to-r text-white flex items-center", theme.primary)}
                >
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className={cn("px-8 py-2 rounded-md border border-current", theme.secondary)}
                >
                  Sign In
                </button>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative w-full max-w-[450px] aspect-video rounded-xl overflow-hidden shadow-2xl">
                <img
                  alt="Influencer video call with fan"
                  className="object-cover w-full h-full"
                  src="/placeholder.svg?height=500&width=800"
                />
                <div
                  className={cn("absolute inset-0 bg-gradient-to-tr from-current/20 to-transparent", theme.secondary)}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Influencers Section */}
      <section className="w-full pt-0 pb-6">
        <div className="container mx-auto px-1">
          <div className="flex flex-col items-center justify-center space-y-4 text-center mb-10">
            <div className="space-y-2">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">Featured Influencers</h2>
              <p className="max-w-[900px] text-gray-600 md:text-xl/relaxed">
                Connect with these trending personalities on Nexbii
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((id) => (
              <div key={id} className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                <div className="aspect-square relative">
                  <img
                    src={`/placeholder.svg?height=300&width=300`}
                    alt="Featured Influencer"
                    className="object-cover w-full h-full"
                  />
                  <div className="absolute top-3 right-3 bg-white rounded-full px-3 py-1 text-sm font-medium shadow-md">
                    2.5M followers
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-bold">Featured Star</h3>
                    <div className="flex items-center text-yellow-500">
                      <Star className="h-4 w-4 fill-current" />
                      <Star className="h-4 w-4 fill-current" />
                      <Star className="h-4 w-4 fill-current" />
                      <Star className="h-4 w-4 fill-current" />
                      <Star className="h-4 w-4 fill-current" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      lifestyle
                    </span>
                    <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      fashion
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={cn("font-medium", theme.secondary)}>Starting at $10/min</span>
                    <button
                      onClick={() => navigate('/register')}
                      className={cn("px-4 py-2 rounded-md bg-gradient-to-r text-white text-sm", theme.primary)}
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-10">
            <button
              onClick={() => navigate('/register')}
              className={cn("px-8 py-2 rounded-md bg-gradient-to-r text-white flex items-center", theme.primary)}
            >
              View All Influencers
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="w-full py-6 md:py-12 bg-gray-50">
        <div className="container mx-auto px-1">
          <div className="flex flex-col items-center justify-center space-y-4 text-center">
            <div className="space-y-2">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">How Nexbii Works</h2>
              <p className="max-w-[900px] text-gray-600 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                Our innovative platform makes connecting with influencers simple, transparent, and exciting.
              </p>
            </div>
          </div>
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-3 lg:gap-12 mt-8">
            {/* Step 1 */}
            <div className="flex flex-col items-center space-y-4 text-center">
              <div
                className={cn("flex h-16 w-16 items-center justify-center rounded-full bg-current/10", theme.secondary)}
              >
                <svg className={cn("h-8 w-8", theme.secondary)} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold">Bid Your Rate</h3>
                <p className="text-gray-600">
                  Place bids on your favorite influencer's time. Higher bids increase your chances of getting selected.
                </p>
              </div>
            </div>
            {/* Step 2 */}
            <div className="flex flex-col items-center space-y-4 text-center">
              <div
                className={cn("flex h-16 w-16 items-center justify-center rounded-full bg-current/10", theme.secondary)}
              >
                <svg className={cn("h-8 w-8", theme.secondary)} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold">Pay Per Minute</h3>
                <p className="text-gray-600">
                  Only pay for the time you spend with the influencer. Our timer ensures transparent billing.
                </p>
              </div>
            </div>
            {/* Step 3 */}
            <div className="flex flex-col items-center space-y-4 text-center">
              <div
                className={cn("flex h-16 w-16 items-center justify-center rounded-full bg-current/10", theme.secondary)}
              >
                <svg className={cn("h-8 w-8", theme.secondary)} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold">Connect Live</h3>
                <p className="text-gray-600">
                  Enjoy high-quality video calls, messaging, or voice chats with your chosen influencer.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full py-6 md:py-12">
        <div className="container mx-auto px-1">
          <div className="flex flex-col items-center justify-center space-y-4 text-center">
            <div className="space-y-2">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">Ready to Connect?</h2>
              <p className="max-w-[900px] text-gray-600 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                Join Nexbii today and start connecting with your favorite influencers or monetize your own following.
              </p>
            </div>
            <div className="flex flex-col gap-2 min-[400px]:flex-row">
              <button
                onClick={() => navigate('/register')}
                className={cn("px-8 py-2 rounded-md bg-gradient-to-r text-white flex items-center", theme.primary)}
              >
                Sign Up Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
              <button
                onClick={() => navigate('/login')}
                className={cn("px-8 py-2 rounded-md border border-current", theme.secondary)}
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* <footer className="w-full py-6 border-t">
        <div className="container mx-auto px-1">
          <div className="flex flex-col items-center space-y-3 text-center text-sm text-gray-500">
            <div className="flex items-center space-x-4">
              <Link to="/terms" className={cn("hover:underline transition-colors", theme.secondary)}>
                Terms of Service
              </Link>
              <span>•</span>
              <Link to="/privacy" className={cn("hover:underline transition-colors", theme.secondary)}>
                Privacy Policy
              </Link>
              <span>•</span>
              <a href="mailto:info@nexbii.com" className={cn("hover:underline transition-colors", theme.secondary)}>
                Contact
              </a>
            </div>
            <div>
              © {new Date().getFullYear()} Nexbii. All rights reserved.
            </div>
          </div>
        </div>
      </footer> */}
    </div>
  );
};

export default WelcomeScreen;